import {collection, doc, getDoc, getDocs} from 'firebase/firestore';
import {Analytics, FirestoreCollection} from 'hubbl-shared';
import {firestore} from './firebase';

// Collection name - unified collection for current and historical data

// Simple toggle for mock data - set to true to use mock data, false for real data

const getInterestAnalytics = async (
  companyId: string,
): Promise<Analytics.InterestAnalytics | null> => {
  try {
    const docRef = doc(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.InterestAnalytics,
      'current',
    );
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return docSnap.data() as Analytics.InterestAnalytics;
  } catch (error) {
    console.error('Error fetching interest analytics:', error);
    return null;
  }
};

const getInterestAnalyticsHistory = async (
  companyId: string,
  year?: number,
  month?: number,
): Promise<Analytics.InterestAnalytics[]> => {
  try {
    const collectionRef = collection(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.InterestAnalytics,
    );

    // Get all documents in the collection
    const snapshot = await getDocs(collectionRef);
    const history: Analytics.InterestAnalytics[] = [];

    snapshot.forEach(docSnap => {
      const data = docSnap.data() as Analytics.InterestAnalytics;

      // Skip the 'current' document and only process snapshots
      if (docSnap.id === 'current' || !data.snapshotDate) {
        return;
      }

      // Apply filters
      if (year !== undefined && data.year !== year) {
        return;
      }
      if (month !== undefined && data.month !== month) {
        return;
      }

      history.push(data);
    });

    // Sort by year and month descending
    history.sort((a, b) => {
      if (a.year !== b.year) {
        return b.year - a.year;
      }
      return b.month - a.month;
    });

    return history;
  } catch (error) {
    console.error('Error fetching interest analytics history:', error);
    return [];
  }
};

const getInterestTrends = async (
  companyId: string,
  months: number = 6,
): Promise<Analytics.InterestAnalytics[]> => {
  try {
    const collectionRef = collection(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.InterestAnalytics,
    );

    // Get all documents in the collection
    const snapshot = await getDocs(collectionRef);
    const trends: Analytics.InterestAnalytics[] = [];

    snapshot.forEach(docSnap => {
      const data = docSnap.data() as Analytics.InterestAnalytics;

      // Skip the 'current' document and only process snapshots
      if (docSnap.id === 'current' || !data.snapshotDate) {
        return;
      }

      trends.push(data);
    });

    // Sort by year and month descending, then take the most recent months
    trends.sort((a, b) => {
      if (a.year !== b.year) {
        return b.year - a.year;
      }
      return b.month - a.month;
    });

    return trends.slice(0, months);
  } catch (error) {
    console.error('Error fetching interest trends:', error);
    return [];
  }
};

const migrateInterestAnalytics = async (
  companyId: string,
): Promise<{success: boolean; message: string}> => {
  try {
    // For migration, we still need to call the Cloud Function
    // since it's a one-time operation that processes all users
    const {httpsCallable} = await import('firebase/functions');
    const {firebaseFunctions} = await import('./firebase');

    const migrateFunction = httpsCallable(
      firebaseFunctions,
      'onMigrateInterestAnalytics',
    );
    const result = await migrateFunction({companyId});
    return result.data as {success: boolean; message: string};
  } catch (error) {
    console.error('Error migrating interest analytics:', error);
    throw error;
  }
};

export const interestAnalytics = {
  getInterestAnalytics,
  getInterestAnalyticsHistory,
  getInterestTrends,
  migrateInterestAnalytics,
};
